import { CogIcon, DatabaseIcon, TableIcon } from "@heroicons/react/outline";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import FrenzyBackground from "../FrenzyBackground";
import Navbar from "../Navbar";

/**
 * Renders the "/" page
 * @returns html containing a page that gives the user information about the game
 */
const Home = () => {
  useEffect(() => {
    document.title = "Home - FPS Aim Trainer";
  }, []);

  // features to be displayed om the home page
  const features = [
    {
      name: "Cumulative Data",
      description:
        "Once logged in, every round you play will be stored, allowing you to view your data over time along with suggestions on how to improve.",
      linkName: "Data",
      linkPath: "/data",
      icon: <DatabaseIcon className="h-8" />,
    },
    {
      name: "Settings",
      description:
        "Play Iron Sights or Duels in easy, medium, or hard mode. Or choose custom mode. Custom mode will use the settings configured in '/settings' to personalize your aim training.",
      linkName: "Settings",
      linkPath: "/settings",
      icon: <CogIcon className="h-8" />,
    },
    {
      name: "Leaderboards",
      description:
        "Compare your high scores with others by viewing the leaderboards. These leaderboards gameifies your training, encouraging more time spent practicing.",
      linkName: "Leaderboards",
      linkPath: "/leaderboards",
      icon: <TableIcon className="h-8" />,
    },
  ];

  // footer's links on left side
  const lLinks = [
    {
      name: "Home",
      href: "/",
    },
    {
      name: "Iron Sights",
      href: "/iron-sights",
    },
    {
      name: "Duels",
      href: "/duels",
    },
    {
      name: "Login",
      href: "/login",
    },
  ];

  // footer's links on right side
  const rLinks = [
    {
      name: "Data",
      href: "/data",
    },
    {
      name: "Leaderboards",
      href: "/leaderboards",
    },
    {
      name: "Settings",
      href: "/settings",
    },
    {
      name: "User Data",
      href: "/user-data",
    },
  ];

  return (
    <>
      <Navbar />
      <div className="flex flex-col items-center">
        <div className="px-6 pt-16 text-center max-w-xl">
          <h1 className="text-3xl font-bold text-gray-800 md:text-4xl">
            A Generalized <span className="text-indigo-500">Aim Trainer</span>{" "}
            with Precise Statistical Feedback
          </h1>
          <p className="mt-6 text-gray-500">
            Train in Iron Sights to improve your accuracy with moving targets or
            train in Duels to improve your response timing with static targets.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link
              to="/iron-sights"
              className="w-24 px-2 py-3 mt-6 text-md font-medium leading-5 text-center text-white capitalize bg-red-500 rounded-lg hover:bg-red-600 focus:outline-none"
            >
              Play Iron Sights &#8594;
            </Link>
            <Link
              to="/duels"
              className="w-24 px-2 py-3 mt-6 text-md font-medium leading-5 text-center text-white capitalize bg-red-500 rounded-lg hover:bg-red-600 focus:outline-none"
            >
              Play Duels &#8594;
            </Link>
          </div>
        </div>
        <div className="h-96 mt-8 mb-8 max-w-3xl flex w-8/12">
          <FrenzyBackground
            leftBound={-11}
            rightBound={11}
            abs={null}
            time={100}
          />
        </div>
        <div className="px-5 md:px-2 lg:px-5 pt-10 pb-8 mx-auto from-indigo-500 bg-gradient-to-b to-blue-400">
          <div className="text-center">
            <h1 className="sm:text-3xl text-2xl font-semibold title-font text-gray-900 mb-4">
              About
            </h1>
            <p className="leading-relaxed xl:w-2/4 lg:w-3/4 mx-auto text-gray-900 text-lg">
              Having good aim is a skill that competitive FPS gamers routinely
              work on. However, few resources target building the muscle memory
              needed for having responsive and accurate all-around aim. Some fps
              games have custom worlds which don't generalize to other fps
              games, and most other aim trainers provide over-generalized
              statistics and feedback. We've created 2 unique game modes along
              with the following features to create a personalized and
              productive aim trainer.
            </p>
            <div className="flex mt-6 justify-center">
              <div className="w-16 h-1 rounded-full bg-black inline-flex"></div>
            </div>
            <div className="grid grid-cols-1 gap-8 mt-8 md:gap-4 xl:mt-12 xl:gap-16 md:grid-cols-3">
              {features.map((feature) => (
                <div
                  className="flex flex-col items-center p-6 space-y-3 text-center bg-gray-100 rounded-xl"
                  key={feature.name}
                >
                  {feature.icon}

                  <h1 className="text-2xl font-semibold text-indigo-500 capitalize ">
                    {feature.name}
                  </h1>

                  <p className="text-gray-700">{feature.description}</p>

                  <Link
                    to={feature.linkPath}
                    className="flex items-center -mx-1 text-sm text-blue-500 capitalize transition-colors duration-200 transform  hover:underline hover:text-blue-600"
                  >
                    {feature.linkName} &#8594;
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <footer className="bg-white">
        <div className="px-6 py-4 mx-auto">
          <div className="sm:flex items-center">
            <div className="w-full lg:w-2/5">
              <div className="px-6">
                <div>
                  <p className="text-xl font-bold text-gray-800 hover:text-gray-700">
                    FPS Aim Trainer
                  </p>
                </div>
                <p className="max-w-md mt-2 text-gray-500">
                  Developed as a Brown CS0320: Software Engineering final
                  project through a shared interest in fps games and statistical
                  analysis.
                </p>
              </div>
            </div>
            <div className="mt-6 lg:mt-0 lg:flex-1">
              <div className="flex gap-x-40 justify-center">
                <div>
                  <h3 className="text-gray-700 font-semibold">About</h3>
                  {lLinks.map((link) => (
                    <Link
                      to={link.href}
                      key={link.name}
                      className="block mt-2 text-sm text-gray-600 hover:underline"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
                <div>
                  <h3 className="text-gray-700 font-semibold">Data</h3>
                  {rLinks.map((link) => (
                    <Link
                      to={link.href}
                      key={link.name}
                      className="block mt-2 text-sm text-gray-600 hover:underline"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Home;
